/**
 * Utility functions for offline storage and background sync
 */

// Check if the browser supports IndexedDB
const isIndexedDBSupported = () => {
  return 'indexedDB' in window;
};

// Check if the browser supports Service Worker
const isServiceWorkerSupported = () => {
  return 'serviceWorker' in navigator;
};

// Check if the browser supports Background Sync
const isBackgroundSyncSupported = () => {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
};

// Store data in offline storage
export const storeOfflineData = async (key: string, data: any): Promise<boolean> => {
  if (!isServiceWorkerSupported()) {
    console.warn('Service Worker is not supported in this browser');
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const swController = registration.active;
    
    if (!swController) {
      console.warn('No active service worker found');
      return false;
    }
    
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          console.error('Error storing offline data:', event.data.error);
          resolve(false);
        } else {
          resolve(event.data.success);
        }
      };
      
      swController.postMessage(
        {
          type: 'STORE_OFFLINE_DATA',
          key,
          data,
        },
        [messageChannel.port2]
      );
    });
  } catch (error) {
    console.error('Failed to store offline data:', error);
    return false;
  }
};

// Retrieve data from offline storage
export const getOfflineData = async <T>(key: string): Promise<T | null> => {
  if (!isServiceWorkerSupported()) {
    console.warn('Service Worker is not supported in this browser');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const swController = registration.active;
    
    if (!swController) {
      console.warn('No active service worker found');
      return null;
    }
    
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          console.error('Error retrieving offline data:', event.data.error);
          resolve(null);
        } else {
          resolve(event.data.data);
        }
      };
      
      swController.postMessage(
        {
          type: 'GET_OFFLINE_DATA',
          key,
        },
        [messageChannel.port2]
      );
    });
  } catch (error) {
    console.error('Failed to retrieve offline data:', error);
    return null;
  }
};

// Save a booking for background sync
export const saveBookingForSync = async (booking: any): Promise<boolean> => {
  try {
    // First, get existing pending bookings
    const pendingBookings = await getOfflineData<any[]>('pending-bookings') || [];
    
    // Add new booking with a client-generated ID
    const newBooking = {
      ...booking,
      id: `offline-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      createdAt: new Date().toISOString(),
    };
    
    const updatedBookings = [...pendingBookings, newBooking];
    
    // Store updated bookings
    const success = await storeOfflineData('pending-bookings', updatedBookings);
    
    if (success && isBackgroundSyncSupported()) {
      // Register for background sync
      const registration = await navigator.serviceWorker.ready;
      if ('sync' in registration) {
        await registration.sync.register('pending-bookings');
      }
    }
    
    return success;
  } catch (error) {
    console.error('Failed to save booking for sync:', error);
    return false;
  }
};

// Get pending bookings
export const getPendingBookings = async (): Promise<any[]> => {
  try {
    return await getOfflineData<any[]>('pending-bookings') || [];
  } catch (error) {
    console.error('Failed to get pending bookings:', error);
    return [];
  }
};

// Listen for sync events from the service worker
export const listenForSyncEvents = (callback: (event: MessageEvent) => void): () => void => {
  if (!isServiceWorkerSupported()) {
    return () => {};
  }
  
  const listener = (event: MessageEvent) => {
    if (event.data && (
      event.data.type === 'SYNC_SUCCESS' || 
      event.data.type === 'BOOKING_SYNCED'
    )) {
      callback(event);
    }
  };
  
  navigator.serviceWorker.addEventListener('message', listener);
  
  // Return a function to remove the listener
  return () => {
    navigator.serviceWorker.removeEventListener('message', listener);
  };
};

// Check app's online status
export const isOnline = (): boolean => {
  return navigator.onLine;
};

// Listen for online/offline events
export const listenForConnectivityChanges = (
  onOnline: () => void,
  onOffline: () => void
): () => void => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  // Return a function to remove the listeners
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

// Prefetch and cache data for offline use
export const prefetchAndCacheData = async (url: string, key: string): Promise<boolean> => {
  if (!isServiceWorkerSupported()) {
    console.warn('Service Worker is not supported in this browser');
    return false;
  }
  
  try {
    // Fetch the data
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
    }
    
    // Parse the data
    const data = await response.json();
    
    // Store in offline storage
    return await storeOfflineData(key, data);
  } catch (error) {
    console.error('Failed to prefetch and cache data:', error);
    return false;
  }
};

// Prefetch and cache multiple resources
export const prefetchResources = async (resources: Array<{ url: string, key: string }>): Promise<boolean[]> => {
  return Promise.all(resources.map(resource => prefetchAndCacheData(resource.url, resource.key)));
};

// Cache an image for offline use
export const cacheImage = async (imageUrl: string): Promise<boolean> => {
  if (!isServiceWorkerSupported()) {
    console.warn('Service Worker is not supported in this browser');
    return false;
  }
  
  try {
    // Try to fetch the image to trigger caching
    await fetch(imageUrl);
    return true;
  } catch (error) {
    console.error('Failed to cache image:', error);
    return false;
  }
};
